/**
 * Popover Component
 * A comprehensive popover component with variants, responsive behavior,
 * and full Ant Design prop support.
 */

import React, { useMemo, useCallback } from "react";
import { Popover as AntPopover } from "antd";
import type { PopoverProps as AntPopoverProps } from "antd";
import { X } from "lucide-react";
import { colors } from "../../theme/colors";
import { cn } from "@/lib/utils";
import { Button } from "../button/Button";
import { Heading } from "../heading/heading";
import { Typography } from "../typography/Typography";

// ============================================================================
// Types & Interfaces
// ============================================================================

export type PopoverPlacement =
  | "top"
  | "topLeft"
  | "topRight"
  | "bottom"
  | "bottomLeft"
  | "bottomRight"
  | "left"
  | "leftTop"
  | "leftBottom"
  | "right"
  | "rightTop"
  | "rightBottom";

export type PopoverTrigger = "hover" | "focus" | "click" | "contextMenu";

export type PopoverVariant =
  | "default"
  | "primary"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "light"
  | "dark";

export type PopoverSize = "small" | "medium" | "large";

export interface PopoverAction {
  /** Label for the action button */
  label: string;
  /** Callback when button is clicked */
  onClick: () => void;
  /** Button variant */
  variant?: "primary" | "secondary" | "danger" | "ghost";
  /** Whether button is loading */
  loading?: boolean;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Custom class for button */
  className?: string;
}

export interface PopoverProps
  extends Omit<
    AntPopoverProps,
    "color" | "overlayStyle" | "overlayClassName" | "content" | "title"
  > {
  /** Title of the popover */
  title?: React.ReactNode;
  /** Content of the popover - can be ReactNode or render function */
  content?: React.ReactNode | (() => React.ReactNode);
  /** Element that triggers the popover */
  children: React.ReactElement;
  /** Popover placement relative to trigger */
  placement?: PopoverPlacement;
  /** How popover is triggered */
  trigger?: PopoverTrigger | PopoverTrigger[];
  /** Color variant of popover */
  variant?: PopoverVariant;
  /** Size of popover */
  size?: PopoverSize;
  /** Whether to show arrow */
  arrow?: boolean;
  /** Maximum width of popover */
  maxWidth?: number | string;
  /** Minimum width of popover */
  minWidth?: number | string;
  /** Whether popover is disabled */
  disabled?: boolean;
  /** Delay before showing popover (ms) */
  showDelay?: number;
  /** Delay before hiding popover (ms) */
  hideDelay?: number;
  /** Custom class for popover overlay */
  className?: string;
  /** Whether popover should be responsive (auto-adjust on mobile) */
  responsive?: boolean;
  /** Z-index of popover */
  zIndex?: number;
  /** Callback when visibility changes */
  onOpenChange?: (open: boolean) => void;
  /** Controlled visibility */
  open?: boolean;
  /** Offset from trigger element [horizontal, vertical] */
  offset?: [number, number];
  /** Action buttons at the bottom of popover */
  actions?: PopoverAction[];
  /** Whether to close on action click */
  closeOnAction?: boolean;
  /** Whether to show close button in header */
  showCloseButton?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
  /** Custom footer content */
  footer?: React.ReactNode;
  /** Whether to destroy popover on hide */
  destroyOnHide?: boolean;
  /** Whether popover is a confirmation dialog */
  isConfirmation?: boolean;
  /** Confirmation text for confirm button */
  confirmText?: string;
  /** Cancel text for cancel button */
  cancelText?: string;
  /** Callback when confirmed */
  onConfirm?: () => void;
  /** Callback when cancelled */
  onCancel?: () => void;
  /** Whether confirm action is loading */
  confirmLoading?: boolean;
  /** Button variant for the confirm button (defaults to "danger") */
  confirmVariant?: "primary" | "danger" | "warning";
  /** Custom styles for popover body */
  bodyStyle?: React.CSSProperties;
  /** Custom styles for popover header */
  headerStyle?: React.CSSProperties;
  /** Custom styles for popover footer */
  footerStyle?: React.CSSProperties;
  /** Whether to prevent default on trigger click */
  preventDefault?: boolean;
}

// ============================================================================
// Constants & Configurations
// ============================================================================

// Variant color configurations
const variantColors: Record<
  PopoverVariant,
  { bg: string; text: string; border: string; titleColor: string }
> = {
  default: {
    bg: colors.white,
    text: colors.textDefault,
    border: colors.surfaceSubtle,
    titleColor: colors.textDefault,
  },
  primary: {
    bg: colors.white,
    text: colors.textDefault,
    border: colors.primary,
    titleColor: colors.primary,
  },
  success: {
    bg: colors.white,
    text: colors.textDefault,
    border: colors.success,
    titleColor: colors.successStrong,
  },
  warning: {
    bg: colors.white,
    text: colors.textDefault,
    border: colors.warning,
    titleColor: colors.warningStrong,
  },
  error: {
    bg: colors.white,
    text: colors.textDefault,
    border: colors.error,
    titleColor: colors.error,
  },
  info: {
    bg: colors.white,
    text: colors.textDefault,
    border: colors.info,
    titleColor: colors.info,
  },
  light: {
    bg: colors.surfaceLightest,
    text: colors.textDefault,
    border: colors.surfaceSubtle,
    titleColor: colors.textDefault,
  },
  dark: {
    bg: colors.textInverse,
    text: colors.white,
    border: colors.textInverse,
    titleColor: colors.white,
  },
};

// Size configurations
const sizeConfig: Record<
  PopoverSize,
  {
    padding: string;
    fontSize: string;
    titleFontSize: string;
    maxWidth: number;
    minWidth: number;
    gap: string;
  }
> = {
  small: {
    padding: "12px",
    fontSize: "13px",
    titleFontSize: "14px",
    maxWidth: 240,
    minWidth: 160,
    gap: "8px",
  },
  medium: {
    padding: "16px",
    fontSize: "14px",
    titleFontSize: "15px",
    maxWidth: 320,
    minWidth: 200,
    gap: "12px",
  },
  large: {
    padding: "20px",
    fontSize: "14px",
    titleFontSize: "16px",
    maxWidth: 420,
    minWidth: 280,
    gap: "16px",
  },
};

// Responsive placement mapping for mobile
const responsivePlacementMap: Record<PopoverPlacement, PopoverPlacement> = {
  top: "top",
  topLeft: "top",
  topRight: "top",
  bottom: "bottom",
  bottomLeft: "bottom",
  bottomRight: "bottom",
  left: "bottom",
  leftTop: "bottom",
  leftBottom: "bottom",
  right: "bottom",
  rightTop: "bottom",
  rightBottom: "bottom",
};

// Heading size mapping
const headingSizeMap: Record<PopoverSize, "xs" | "sm" | "md"> = {
  small: "xs",
  medium: "sm",
  large: "md",
};

// ============================================================================
// Component
// ============================================================================

export const Popover: React.FC<PopoverProps> = ({
  title,
  content,
  children,
  placement = "bottom",
  trigger = "click",
  variant = "default",
  size = "medium",
  arrow = true,
  maxWidth,
  minWidth,
  disabled = false,
  showDelay = 100,
  hideDelay = 100,
  className,
  responsive = true,
  zIndex = 1050,
  onOpenChange,
  open,
  offset,
  actions,
  closeOnAction = true,
  showCloseButton = false,
  onClose,
  footer,
  destroyOnHide = true,
  isConfirmation = false,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  confirmLoading = false,
  confirmVariant = "danger",
  bodyStyle,
  headerStyle,
  footerStyle,
  preventDefault = false,
  ...props
}) => {
  // Detect if on mobile device
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  }, []);

  // Get responsive placement
  const effectivePlacement = useMemo(() => {
    if (responsive && isMobile) {
      return responsivePlacementMap[placement];
    }
    return placement;
  }, [placement, responsive, isMobile]);

  // Get variant colors
  const colorConfig = variantColors[variant];
  const sizeSettings = sizeConfig[size];

  // Calculate effective dimensions
  const effectiveMaxWidth = useMemo(() => {
    if (maxWidth) {
      return typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth;
    }
    if (responsive && isMobile) {
      return `${Math.min(sizeSettings.maxWidth, window.innerWidth - 32)}px`;
    }
    return `${sizeSettings.maxWidth}px`;
  }, [maxWidth, responsive, isMobile, sizeSettings.maxWidth]);

  const effectiveMinWidth = useMemo(() => {
    if (minWidth) {
      return typeof minWidth === "number" ? `${minWidth}px` : minWidth;
    }
    return `${sizeSettings.minWidth}px`;
  }, [minWidth, sizeSettings.minWidth]);

  // Handle close
  const handleClose = useCallback(() => {
    onClose?.();
    onOpenChange?.(false);
  }, [onClose, onOpenChange]);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    onConfirm?.();
    if (closeOnAction) {
      onOpenChange?.(false);
    }
  }, [onConfirm, closeOnAction, onOpenChange]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onCancel?.();
    onOpenChange?.(false);
  }, [onCancel, onOpenChange]);

  // Handle action click
  const handleActionClick = useCallback(
    (action: PopoverAction) => {
      action.onClick();
      if (closeOnAction) {
        onOpenChange?.(false);
      }
    },
    [closeOnAction, onOpenChange]
  );

  // Build align configuration based on placement
  const alignConfig = useMemo(() => {
    if (offset) {
      return { offset };
    }
    // Adjust offset based on placement to ensure arrow points at trigger center
    // For "Right" placements, shift popover left so arrow aligns with trigger
    // For "Left" placements, shift popover right so arrow aligns with trigger
    const placementOffsets: Partial<Record<PopoverPlacement, [number, number]>> = {
      bottomRight: [56, 8],  // Shift left to align arrow with trigger
      bottomLeft: [-56, 8],  // Shift right to align arrow with trigger
      topRight: [56, -8],
      topLeft: [-56, -8],
    };
    return { offset: placementOffsets[effectivePlacement] || [0, 8] };
  }, [offset, effectivePlacement]);

  // Build popover content
  const popoverContent = useMemo(() => {
    const resolvedContent =
      typeof content === "function" ? content() : content;

    // Wrap string content with Typography for consistent styling
    const renderContent = () => {
      if (typeof resolvedContent === "string") {
        return (
          <Typography
            variant="body2"
            color={colorConfig.text}
          >
            {resolvedContent}
          </Typography>
        );
      }
      return resolvedContent;
    };

    return (
      <div
        className="nf-popover-body"
        style={{
          fontSize: sizeSettings.fontSize,
          color: colorConfig.text,
          lineHeight: 1.5,
          ...bodyStyle,
        }}
      >
        {renderContent()}

        {/* Confirmation Buttons */}
        {isConfirmation && (
          <div
            className="flex items-center justify-end gap-2"
            style={{ marginTop: sizeSettings.gap }}
          >
            <Button
              variant="secondary"
              size="small"
              onClick={handleCancel}
            >
              {cancelText}
            </Button>
            <Button
              variant={confirmVariant}
              size="small"
              onClick={handleConfirm}
              loading={confirmLoading}
              disabled={confirmLoading}
            >
              {confirmText}
            </Button>
          </div>
        )}

        {/* Action Buttons */}
        {actions && actions.length > 0 && !isConfirmation && (
          <div
            className="flex items-center justify-end gap-2 flex-wrap"
            style={{ marginTop: sizeSettings.gap }}
          >
            {actions.map((action, index) => {
              // Map popover action variants to Button variants
              const buttonVariant = action.variant === "primary" || action.variant === "danger"
                ? "danger"
                : action.variant === "ghost"
                  ? "ghost"
                  : "secondary";

              return (
                <Button
                  key={index}
                  variant={buttonVariant}
                  size="small"
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                  loading={action.loading}
                  className={action.className}
                >
                  {action.label}
                </Button>
              );
            })}
          </div>
        )}

        {/* Custom Footer */}
        {footer && (
          <div
            className="nf-popover-footer"
            style={{
              marginTop: sizeSettings.gap,
              paddingTop: sizeSettings.gap,
              borderTop: `1px solid ${colors.surfaceSubtle}`,
              ...footerStyle,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    );
  }, [
    content,
    sizeSettings,
    colorConfig,
    bodyStyle,
    isConfirmation,
    confirmText,
    cancelText,
    confirmLoading,
    confirmVariant,
    handleConfirm,
    handleCancel,
    actions,
    handleActionClick,
    footer,
    footerStyle,
  ]);

  // Build popover title
  const popoverTitle = useMemo(() => {
    if (!title && !showCloseButton) return undefined;

    return (
      <div
        className="nf-popover-header flex items-center justify-between"
        style={{
          ...headerStyle,
        }}
      >
        {title && (
          <Heading
            level={5}
            size={headingSizeMap[size]}
            weight="semibold"
            noMargin
            color={colorConfig.titleColor}
          >
            {title}
          </Heading>
        )}
        {showCloseButton && (
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded transition-colors cursor-pointer hover:bg-gray-100"
            style={{ color: colors.textMuted }}
            aria-label="Close popover"
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}
      </div>
    );
  }, [
    title,
    showCloseButton,
    size,
    colorConfig.titleColor,
    headerStyle,
    handleClose,
  ]);

  // Build overlay styles
  const overlayInnerStyle: React.CSSProperties = useMemo(
    () => ({
      backgroundColor: colorConfig.bg,
      color: colorConfig.text,
      padding: sizeSettings.padding,
      borderRadius: "8px",
      boxShadow: "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
      border: `1px solid ${colorConfig.border}`,
      maxWidth: effectiveMaxWidth,
      minWidth: effectiveMinWidth,
      wordWrap: "break-word",
    }),
    [colorConfig, sizeSettings, effectiveMaxWidth, effectiveMinWidth]
  );

  // Don't render popover if disabled
  if (disabled) {
    return children;
  }

  // Clone the child and ensure it can receive events
  const childElement = React.isValidElement(children) ? (
    <span
      style={{ display: "inline-flex" }}
      onClick={preventDefault ? (e) => e.preventDefault() : undefined}
    >
      {children}
    </span>
  ) : (
    <span>{children}</span>
  );

  return (
    <AntPopover
      title={popoverTitle}
      content={popoverContent}
      placement={effectivePlacement}
      trigger={trigger}
      arrow={arrow ? { pointAtCenter: true } : false}
      mouseEnterDelay={showDelay / 1000}
      mouseLeaveDelay={hideDelay / 1000}
      align={alignConfig}
      classNames={{
        root: cn(
          "nf-popover",
          `nf-popover-${variant}`,
          `nf-popover-${size}`,
          className
        ),
      }}
      styles={{
        body: overlayInnerStyle,
      }}
      zIndex={zIndex}
      onOpenChange={onOpenChange}
      open={open}
      destroyTooltipOnHide={destroyOnHide}
      fresh
      {...props}
    >
      {childElement}
    </AntPopover>
  );
};

// ============================================================================
// Compound Components
// ============================================================================

/** Confirmation Popover - pre-configured for delete/confirm actions */
export interface ConfirmPopoverProps
  extends Omit<PopoverProps, "isConfirmation" | "actions" | "footer"> {
  /** Title of the confirmation */
  title: string;
  /** Description text */
  description?: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Callback when confirmed */
  onConfirm: () => void;
  /** Callback when cancelled */
  onCancel?: () => void;
  /** Whether confirm action is loading */
  confirmLoading?: boolean;
  /** Variant - typically error for delete actions */
  variant?: PopoverVariant;
}

export const ConfirmPopover: React.FC<ConfirmPopoverProps> = ({
  title,
  description,
  confirmText = "Delete",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  confirmLoading = false,
  variant = "default",
  children,
  ...props
}) => {
  return (
    <Popover
      title={title}
      content={description}
      isConfirmation
      confirmText={confirmText}
      cancelText={cancelText}
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      variant={variant}
      {...props}
    >
      {children}
    </Popover>
  );
};

/** Info Popover - pre-configured info variant */
export const InfoPopover: React.FC<Omit<PopoverProps, "variant">> = (props) => (
  <Popover variant="info" {...props} />
);

/** Success Popover - pre-configured success variant */
export const SuccessPopover: React.FC<Omit<PopoverProps, "variant">> = (
  props
) => <Popover variant="success" {...props} />;

/** Warning Popover - pre-configured warning variant */
export const WarningPopover: React.FC<Omit<PopoverProps, "variant">> = (
  props
) => <Popover variant="warning" {...props} />;

/** Error Popover - pre-configured error variant */
export const ErrorPopover: React.FC<Omit<PopoverProps, "variant">> = (
  props
) => <Popover variant="error" {...props} />;

/** Dark Popover - pre-configured dark variant */
export const DarkPopover: React.FC<Omit<PopoverProps, "variant">> = (props) => (
  <Popover variant="dark" {...props} />
);

export default Popover;
